/**
 * Platform surfaces embedded inside the Mandate shell (Individual Space).
 * Reuses the same page components as /prioritization/* with /mandate/* paths.
 */

import { Route, Outlet } from 'react-router-dom';
import { ItemListView } from '../../components/prioritization/item-list';
import ItemDetailPage from '../prioritization/ItemDetailPage';
import { ItemJourneyPage } from '../prioritization/ItemJourneyPage';
import ItemTypes from '../prioritization/ItemTypes';
import ItemReadiness from '../prioritization/ItemReadiness';
import LineagePage from '../prioritization/LineagePage';
import DecisionsPage from '../prioritization/DecisionsPage';
import { SessionDetail } from '../prioritization/sessions';
import SessionsSurface from '../prioritization/sessions/SessionsSurface';
import SessionResults from '../prioritization/sessions/SessionResults';
import ReportArtifactPage from '../prioritization/sessions/ReportArtifactPage';
import LiveSessionPage from '../prioritization/sessions/LiveSessionPage';
import { SessionParticipantFocus } from '../../components/prioritization/session/SessionParticipantFocus';
import { SessionResultsReport } from '../prioritization/reports/SessionResultsReport';
import ToolsIndex from '../prioritization/Tools';
import StackRanking from '../stack-ranking/StackRanking';
import PairwiseRanking from '../pairwise-ranking/PairwiseRanking';
import DotVoting from '../dot-voting/DotVoting';
import BuyAFeature from '../buy-a-feature/BuyAFeature';
import MoscowMethod from '../moscow-method/MoscowMethod';
import MagicPrioritization from '../magic-prioritization/MagicPrioritization';
import MaxPrioritiesPyramid from '../max-priorities-pyramid/MaxPrioritiesPyramid';
import SimpleWeightedScoring from '../simple-weighted-scoring/SimpleWeightedScoring';
import FastVsRight from '../fast-vs-right/FastVsRight';
import EisenhowerMatrix from '../eisenhower-matrix/EisenhowerMatrix';
import ImpactEffortMatrix from '../impact-effort-matrix/ImpactEffortMatrix';
import RiceScoring from '../rice-scoring/RiceScoring';
import WeightedSmallestJobFirst from '../wsjf/WeightedSmallestJobFirst';
import CostOfDelay from '../cost-of-delay/CostOfDelay';
import ParetoAnalysis from '../pareto-analysis/ParetoAnalysis';
import SpeedBoat from '../speed-boat/SpeedBoat';
import PruneProductTree from '../prune-product-tree/PruneProductTree';

/**
 * Platform route elements for MandateApp's <Routes> tree.
 * Must be a Fragment expression (not a wrapper component) — React Router only
 * accepts <Route> or <React.Fragment> as direct children of <Routes>.
 */
export const mandatePlatformRouteElements = (
  <>
      {/* Items */}
      <Route path="items" element={<ItemListView />} />
      <Route path="items/types" element={<ItemTypes />} />
      <Route path="items/readiness" element={<ItemReadiness />} />
      <Route path="items/:itemId/journey" element={<ItemJourneyPage />} />
      <Route path="items/:itemId" element={<ItemDetailPage />} />

      {/* Sessions */}
      <Route path="sessions" element={<Outlet />}>
        {/* The one Session List (SessionsSurface), the same as /prioritization/sessions;
            the current space scopes it to Mandate sessions (lib/sessions/sessionSpaceScope.ts). */}
        <Route index element={<SessionsSurface />} />
        <Route path=":id/results" element={<SessionResults />} />
        <Route path=":sessionId/reports/:artifactId" element={<ReportArtifactPage />} />
        <Route path=":sessionId/participate" element={<SessionParticipantFocus />} />
        <Route path=":sessionId/live" element={<LiveSessionPage />} />
        <Route path=":sessionId" element={<SessionDetail />} />
      </Route>

      {/* Session reports (canonical reporting surface) */}
      <Route path="reports">
        <Route path="session/:sessionId" element={<SessionResultsReport />} />
      </Route>

      {/* Platform decision log (mirrored mandate decisions + origin_module filter in UI) */}
      <Route path="platform">
        <Route path="decisions" element={<DecisionsPage />} />
      </Route>

      {/* Lineage / provenance feed */}
      <Route path="lineage" element={<LineagePage />} />

      {/* Tools — same registry as prioritization Individual Space */}
      <Route path="tools">
        <Route index element={<ToolsIndex />} />
        <Route path="stack-ranking" element={<StackRanking />} />
        <Route path="pairwise-ranking" element={<PairwiseRanking />} />
        <Route path="dot-voting" element={<DotVoting />} />
        <Route path="buy-a-feature" element={<BuyAFeature />} />
        <Route path="moscow-method" element={<MoscowMethod />} />
        <Route path="magic-prioritization" element={<MagicPrioritization />} />
        <Route path="max-priorities-pyramid" element={<MaxPrioritiesPyramid />} />
        <Route path="simple-weighted-scoring" element={<SimpleWeightedScoring />} />
        <Route path="fast-vs-right" element={<FastVsRight />} />
        <Route path="eisenhower-matrix" element={<EisenhowerMatrix />} />
        <Route path="impact-effort-matrix" element={<ImpactEffortMatrix />} />
        <Route path="rice-scoring" element={<RiceScoring />} />
        <Route path="wsjf" element={<WeightedSmallestJobFirst />} />
        <Route path="cost-of-delay" element={<CostOfDelay />} />
        <Route path="pareto-analysis" element={<ParetoAnalysis />} />
        <Route path="speed-boat" element={<SpeedBoat />} />
        <Route path="prune-product-tree" element={<PruneProductTree />} />
      </Route>
  </>
);
